import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, Star, Calendar, Sparkles, MapPin } from "lucide-react";
import { ASSETS, NAP, SERVICES, AREAS, HIGHLIGHTED_AREAS, WHY_US, LOCAL_AREAS, BOOK_URL } from "../lib/data";
import { TID } from "../lib/testIds";
import Ribbon from "../components/Ribbon";
import FAQ from "../components/FAQ";
import GoogleMap from "../components/GoogleMap";
import CallToActionStrip from "../components/CallToActionStrip";
import { useSEO } from "../lib/useSEO";
import { trackBookClick } from "../lib/analytics";

export default function Home() {
  useSEO({
    title: "Act Fast Appliance Repair — Same-Day Chicago Appliance Repair | (773) 888-4663",
    description:
      "Chicago's trusted same-day appliance repairman for 26+ years. Factory-trained on Sub-Zero, Wolf, LG, Samsung, Whirlpool & more. Parts & labor warranty. Serving 34 communities.",
    path: "/",
  });
  return (
    <div>
      {/* SECTION 1 - HERO */}
      {/* Hero section uses overflow-hidden so the marquee ribbon below never bleeds horizontally on mobile */}
      <section className="relative min-h-[100vh] flex flex-col overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ASSETS.hero}
            alt="Act Fast technician repairing appliance"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 af-hero-gradient" />
        </div>

        <div className="relative pt-36 pb-16 lg:pt-44 lg:pb-32 flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left copy */}
            <div className="lg:col-span-7 af-stagger">
              <p className="af-eyebrow mb-4">Chicago's #1 Repairman</p>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] mb-6 font-display">
                EXPERT <br />
                <span className="text-[#DC2626]">APPLIANCE</span> <br />
                REPAIR.
              </h1>
              <p className="text-base lg:text-lg text-neutral-300 max-w-xl mb-8 leading-relaxed">
                Same-day and next-day appointments available across Chicago and surrounding communities.
                Factory-trained technicians. Parts guaranteed.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a href={NAP.phoneTel} className="af-btn-primary" data-testid={TID.heroCallBtn}>
                  <Phone size={18} /> Call Now
                </a>
                <Link to="/services" className="af-btn-outline" data-testid={TID.heroServicesBtn}>
                  Our Services <ArrowRight size={18} />
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-4 lg:gap-8 max-w-2xl border-t border-[#262626] pt-6">
                <Stat n="26+" label="Years in Business" />
                <Stat n="34" label="Service Areas" />
                <Stat n="5K+" label="Happy Customers" />
              </div>
            </div>

            {/* Right floating card */}
            <div
              className="lg:col-span-5 bg-[#0a0a0a]/95 backdrop-blur border border-[#DC2626]/40 p-7 lg:p-8 shadow-[0_25px_60px_-15px_rgba(220,38,38,0.25)]"
              data-testid="hero-card"
            >
              <div className="flex items-center gap-2 mb-5">
                <Sparkles size={16} className="text-[#DC2626]" />
                <span className="af-eyebrow">What Can We Fix?</span>
              </div>
              <h3 className="text-2xl font-bold mb-5 font-display">Select an appliance</h3>
              <ul className="space-y-2 mb-7">
                {SERVICES.map((s) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.slug}>
                      <Link
                        to={`/services/${s.slug}`}
                        className="group flex items-center justify-between p-3 border border-[#262626] hover:border-[#DC2626] hover:bg-[#141414] transition-all"
                        data-testid={TID.heroApplianceOption(s.slug)}
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={20} className="text-[#DC2626]" />
                          <span className="text-white text-sm font-semibold uppercase tracking-wider">
                            {s.name}
                          </span>
                        </div>
                        <ArrowRight
                          size={16}
                          className="text-neutral-600 group-hover:text-[#DC2626] group-hover:translate-x-1 transition-all"
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackBookClick("hero-card")}
                className="af-btn-primary w-full justify-center"
                data-testid={TID.heroBookBtn}
              >
                Book a Repair Today <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>

        <Ribbon />
      </section>

      {/* SECTION 2 - SERVICES */}
      <section id="services" className="bg-black py-20 lg:py-28" data-testid={TID.sectionServices}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-7">
              <p className="af-eyebrow mb-3">What We Repair</p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] font-display">
                EXPERT REPAIRS FOR <br />
                EVERY <span className="text-[#DC2626]">MAJOR APPLIANCE</span>
              </h2>
            </div>
            <div className="lg:col-span-5 bg-[#0a0a0a] border-l-4 border-[#DC2626] p-6 relative">
              <p className="af-eyebrow mb-2">Brand Expertise</p>
              <p className="text-white text-sm lg:text-base leading-relaxed">
                Authorized with <span className="text-[#DC2626] font-semibold">Hisense</span> &{" "}
                <span className="text-[#DC2626] font-semibold">Smeg</span> Appliance Brand, and specialize
                in <span className="text-[#DC2626] font-semibold">Sub-Zero</span> Brand.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="af-card p-6 flex flex-col group"
                  data-testid={TID.serviceCard(s.slug)}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#141414] border border-[#262626] mb-4 group-hover:border-[#DC2626] group-hover:bg-[#DC2626]/10 transition-all">
                    <Icon size={24} className="text-[#DC2626]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 font-display">{s.name}</h3>
                  <p className="text-sm text-neutral-400 mb-5 leading-relaxed flex-1">{s.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {s.brands.slice(0, 5).map((b) => (
                      <span key={b} className="af-pill">{b}</span>
                    ))}
                  </div>
                  <div className="flex items-center text-[#DC2626] text-xs font-bold uppercase tracking-wider mt-auto">
                    Explore <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 - WHY CHOOSE US */}
      <section className="bg-[#050505] py-20 lg:py-28 border-y border-[#262626]" data-testid={TID.whyChooseUs}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="af-eyebrow mb-3">Why Choose Us</p>
            <h2 className="text-4xl sm:text-5xl font-bold leading-[1.05] font-display mb-10">
              CHICAGOLAND'S TRUSTED <br />
              <span className="text-[#DC2626]">REPAIR SPECIALISTS</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
              {WHY_US.map((item) => (
                <div key={item.num} className="border-l-2 border-[#262626] hover:border-[#DC2626] pl-5 transition-colors">
                  <div className="text-3xl font-black text-[#DC2626]/70 mb-1 font-display">{item.num}</div>
                  <h3 className="text-lg font-bold mb-2 text-white font-display">{item.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative border border-[#262626] bg-[#0a0a0a] p-3">
              <img
                src={ASSETS.section3Van}
                alt="Act Fast service van"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#DC2626] text-white px-5 py-3">
                <div className="text-xs uppercase tracking-widest">Same-Day</div>
                <div className="text-lg font-bold">Across Chicagoland</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 - SERVICE AREAS */}
      <section className="bg-black py-20 lg:py-28" data-testid={TID.serviceAreasSection}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <p className="af-eyebrow mb-3">Where We Serve</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display">
              <span className="text-[#DC2626]">34 COMMUNITIES</span> <br className="lg:hidden" /> ACROSS CHICAGOLAND
            </h2>
            <Link to="/contact" className="af-btn-primary mt-8 inline-flex" data-testid={TID.confirmAreaBtn}>
              <MapPin size={18} /> Confirm Your Area
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {AREAS.map((area) => {
              const highlighted = HIGHLIGHTED_AREAS.includes(area);
              const localData = LOCAL_AREAS.find((l) => l.city === area);
              if (!localData) return null;
              if (highlighted) {
                return (
                  <Link
                    key={area}
                    to={`/areas/${localData.slug}`}
                    data-testid={TID.areaSlot(localData.slug)}
                    className="p-4 text-center border border-[#DC2626] text-white font-bold bg-[#141414] hover:bg-[#DC2626] transition-colors text-sm uppercase tracking-wider flex items-center justify-center gap-1.5 group"
                  >
                    <MapPin size={14} /> {area}
                  </Link>
                );
              }
              return (
                <Link
                  key={area}
                  to={`/areas/${localData.slug}`}
                  data-testid={TID.areaSlot(localData.slug)}
                  className="p-4 text-center border border-[#262626] text-neutral-400 text-sm bg-black hover:border-[#DC2626] hover:text-white transition-colors uppercase tracking-wider"
                >
                  {area}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 5 - CTA */}
      <CallToActionStrip />

      {/* SECTION 6 - MAP */}
      <GoogleMap />

      {/* SECTION 7 - FAQ */}
      <FAQ />
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="text-3xl lg:text-4xl font-extrabold text-[#DC2626] font-display">{n}</div>
      <div className="text-xs lg:text-sm text-neutral-400 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}
